import type React from 'react';
import { useMemo, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import { useAppSelector } from '../hooks';
import {
  selectLegibleTextColor,
  selectSize,
  selectUserMessageBubbleColor,
} from 'reducers/config';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { Timestamp } from './MessageStep/Timestamp';
import { selectIsMobile } from 'reducers/main';
import { motion } from 'motion/react';
import { selectMessageBubblePositions } from 'reducers/workflows';
import type { MessageBubblePosition } from 'utils/getMessageBubblePositions';
import type getMessageBubblePositions from 'utils/getMessageBubblePositions';
import { MESSAGE_PADDING } from 'embed/constants';

interface MessageProps {
  /** Element to display as user message */
  children: React.ReactNode;
  /** used for components like metadata, article, attachments */
  nonMessageChildren?: React.ReactElement;
  stepIndex: number;
  timestamp?: number;
}

const UserMessage: React.FC<React.PropsWithChildren<MessageProps>> = ({
  children,
  nonMessageChildren,
  stepIndex,
  timestamp,
}) => {
  const theme = useTheme();
  const ref = useRef<HTMLDivElement>(null);
  const legibleTextColor = useAppSelector(selectLegibleTextColor);
  const isMobile = useAppSelector(selectIsMobile);
  const size = useAppSelector(selectSize);
  const userMessageBubbleColor = useAppSelector(selectUserMessageBubbleColor);
  const messageBubblePositions = useAppSelector(selectMessageBubblePositions);
  const messageBubblePosition = messageBubblePositions[stepIndex] ?? null;

  const animate = useMemo(
    () => ({
      y: '0px',
    }),
    [],
  );
  const initial = useMemo(
    () => ({
      y: '30px',
    }),
    [],
  );
  const transition = useMemo(
    () => ({
      bounce: 0.35,
      duration: 0.4,
      type: 'spring',
    }),
    [],
  );

  return (
    <Container
      borderColor={theme.palette.colors.white}
      // `user-message` is used in QuickFeedback.tsx
      className='js-user-message user-message'
    >
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          gap: '4px',
          margin:
            size === 'large' && !isMobile ? '0 0 16px 104px' : '0 0 16px 24px',
        }}
      >
        <Timestamp containerSx={{ marginLeft: 'auto' }} timestamp={timestamp} />
        {!!children && (
          <UserMessageContainer
            animate={animate}
            initial={initial}
            messageBubblePosition={messageBubblePosition}
            ref={ref}
            themeColor={userMessageBubbleColor}
            transition={transition}
          >
            <UserContent
              aria-keyshortcuts='ArrowDown ArrowUp'
              className='js-user-message-focusable'
              dir='auto'
              tabIndex={0}
              textColor={legibleTextColor}
              themeColor={userMessageBubbleColor}
            >
              <>
                <p className='visually-hidden'>User Response:</p>
                {children}
              </>
            </UserContent>
          </UserMessageContainer>
        )}
        {nonMessageChildren}
      </Box>
    </Container>
  );
};

const messageBubblePositionToRadiusMap: Record<MessageBubblePosition, string> =
  {
    'first-in-a-batch': '25px 25px 8px 25px',
    'last-in-a-batch': '25px 8px 25px 25px',
    lone: '25px',
    middle: '25px 8px 8px 25px',
  } as const;

const UserMessageContainer = styled(motion.div, {
  shouldForwardProp: prop =>
    prop !== 'messageBubblePosition' && prop !== 'themeColor',
})<{
  messageBubblePosition: ReturnType<typeof getMessageBubblePositions>[string];
  themeColor: string;
}>`
  position: relative;
  padding: ${MESSAGE_PADDING};
  background-color: ${props => props.themeColor};
  border-radius: ${props =>
    props.messageBubblePosition?.user
      ? messageBubblePositionToRadiusMap[props.messageBubblePosition?.user]
      : '25px'};
  align-self: flex-end;
  word-break: break-word;
  width: fit-content;

  ${props =>
    (props.messageBubblePosition?.user === 'first-in-a-batch' ||
      props.messageBubblePosition?.user === 'middle') &&
    'margin-bottom: -12px;'}
`;

const Container = styled('div')<{ borderColor: string }>`
  display: flex;
  justify-content: flex-end;
  &:focus {
    outline: none;
  }
  &:focus-visible {
    border: 1px solid ${props => props.borderColor};
  }
  & + & {
    margin-top: -12px;
  }
`;

const UserContent = styled('div')<{ textColor: string; themeColor: string }>`
  border: 1px solid ${props => props.themeColor};
  background-color: ${props => props.themeColor};
  color: ${props => props.textColor};
  margin: 0;
  line-height: 22px;
  white-space: pre-line; // show new line characters
  &:focus {
    outline: none;
  }
  &:focus-visible {
    border: 1px solid ${props => props.textColor};
  }
`;

export default UserMessage;
